import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  SimpleGrid,
  useToast,
  Heading,
  FormErrorMessage,
  Card,
  CardBody,
  CardHeader,
  Divider,
  VStack,
  HStack,
  Image,
  Badge,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Save, Edit, Upload, X } from "lucide-react";
// IMPORT ACTIONS
import { postData, updateData } from "../../redux/actions/common.action";

interface AccountFormData {
  _id?: string;
  bankName: string;
  accountNo: string;
  branch: string;
  IFSCCode: string;
  accountHolderName: string;
  percentage: string;
  mobileNo: string;
  address: string;
  upiId?: string;
  upiQrCodeImage?: string;
}

const AddAccount = () => {
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const { user, loading } = useSelector((state: any) => state.common || {});

  // Determine Endpoint
  const endpoint = "account";

  const initialState: AccountFormData = location?.state
    ? {
        _id: location.state._id,
        bankName: location.state.bankName || "",
        accountNo: location.state.accountNo || "",
        branch: location.state.branch || "",
        IFSCCode: location.state.IFSCCode || "",
        accountHolderName: location.state.accountHolderName || "",
        percentage: location.state.percentage || "",
        mobileNo: location.state.mobileNo || "",
        address: location.state.address || "",
        upiId: location.state.upiId || "",
        upiQrCodeImage: location.state.upiQrCodeImage || "",
      }
    : {
        bankName: "",
        accountNo: "",
        branch: "",
        IFSCCode: "",
        accountHolderName: "",
        percentage: "",
        mobileNo: "",
        address: "",
        upiId: "",
        upiQrCodeImage: "",
      };

  const [formData, setFormData] = useState<AccountFormData>(initialState);
  const [errors, setErrors] = useState<
    Partial<Record<keyof AccountFormData, string>>
  >({});
  const [upiQrImageUrl, setUpiQrImageUrl] = useState<string | null>(
    location?.state?.upiQrCodeImage || null,
  );
  const [isUploadingImage, setIsUploadingImage] = useState<boolean>(false);

  const validateForm = () => {
    let isValid = true;
    const validationErrors: Partial<Record<keyof AccountFormData, string>> = {};

    if (!formData.bankName.trim()) {
      validationErrors.bankName = "Bank name is required";
      isValid = false;
    }
    if (!formData.accountNo.trim()) {
      validationErrors.accountNo = "Account number is required";
      isValid = false;
    }
    if (!formData.IFSCCode.trim()) {
      validationErrors.IFSCCode = "IFSC code is required";
      isValid = false;
    }
    if (!formData.accountHolderName.trim()) {
      validationErrors.accountHolderName = "Holder Name is required";
      isValid = false;
    }

    setErrors(validationErrors);
    return isValid;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // UPI QR Code Image Upload Handler
  const handleUpiImageChange = (e: any) => {
    setIsUploadingImage(true);
    const formDataObj = new FormData();
    formDataObj.append("file", e.target.files[0]);

    dispatch(postData(formDataObj, "upload-image", navigate, toast))
      .then((data: any) => {
        setUpiQrImageUrl(data?.imageUrl);
        setFormData((prev) => ({ ...prev, upiQrCodeImage: data?.imageUrl }));
        toast({
          title: "Success",
          description: "UPI QR code image uploaded successfully",
          status: "success",
          position: "top-right",
          duration: 2000,
        });
      })
      .catch((error: any) => {
        console.error("Image upload failed:", error);
        toast({
          title: "Error",
          description: "Failed to upload UPI QR code image",
          status: "error",
          position: "top-right",
        });
      })
      .finally(() => {
        setIsUploadingImage(false);
      });
  };

  // Remove UPI Image
  const handleRemoveUpiImage = () => {
    setUpiQrImageUrl(null);
    setFormData((prev) => ({ ...prev, upiQrCodeImage: "" }));
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fill all required fields.",
        status: "error",
        position: "top-right",
      });
      return;
    }

    const payload = {
      ...formData,
      createdBy: user?._id,
      upiQrCodeImage: upiQrImageUrl || undefined,
    };

    if (location?.state?._id) {
      // UPDATE MODE
      dispatch(
        updateData(location.state._id, endpoint, payload, navigate, toast),
      );
    } else {
      // CREATE MODE
      dispatch(postData(payload, endpoint, navigate, toast));
    }
  };

  return (
    <Box p={{ base: 2, md: 6 }} bg="gray.50" minH="100vh">
      <Flex justify="center">
        <Card
          w="full"
          maxW="8xl"
          variant="outline"
          boxShadow="sm"
          borderRadius="xl"
        >
          <CardHeader bg="white" borderBottomWidth="1px" py={4}>
            <Flex justify="space-between" align="center">
              <Heading size="md" color="gray.700">
                {location?.state ? "Update Account Details" : "Add New Account"}
              </Heading>
              <Button
                size="sm"
                leftIcon={<ArrowLeft size={16} />}
                variant="ghost"
                onClick={() => navigate("/account-list")}
              >
                Back to List
              </Button>
            </Flex>
          </CardHeader>

          <CardBody>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
              <FormControl isInvalid={!!errors.bankName} isRequired>
                <FormLabel fontSize="sm" fontWeight="medium">
                  Bank Name
                </FormLabel>
                <Input
                  name="bankName"
                  value={formData.bankName}
                  onChange={handleChange}
                  placeholder="e.g. HDFC Bank"
                  bg="gray.50"
                />
                <FormErrorMessage>{errors.bankName}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.branch}>
                <FormLabel fontSize="sm" fontWeight="medium">
                  Branch Name
                </FormLabel>
                <Input
                  name="branch"
                  value={formData.branch}
                  onChange={handleChange}
                  placeholder="e.g. MG Road Branch"
                  bg="gray.50"
                />
              </FormControl>

              <FormControl isInvalid={!!errors.accountNo} isRequired>
                <FormLabel fontSize="sm" fontWeight="medium">
                  Account Number
                </FormLabel>
                <Input
                  name="accountNo"
                  value={formData.accountNo}
                  onChange={handleChange}
                  type="number"
                  placeholder="Enter Account No"
                  bg="gray.50"
                />
                <FormErrorMessage>{errors.accountNo}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.IFSCCode} isRequired>
                <FormLabel fontSize="sm" fontWeight="medium">
                  IFSC Code
                </FormLabel>
                <Input
                  name="IFSCCode"
                  value={formData.IFSCCode}
                  onChange={handleChange}
                  placeholder="e.g. HDFC0001234"
                  bg="gray.50"
                  textTransform="uppercase"
                />
                <FormErrorMessage>{errors.IFSCCode}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.accountHolderName} isRequired>
                <FormLabel fontSize="sm" fontWeight="medium">
                  Account Holder Name
                </FormLabel>
                <Input
                  name="accountHolderName"
                  value={formData.accountHolderName}
                  onChange={handleChange}
                  placeholder="Enter Name"
                  bg="gray.50"
                />
                <FormErrorMessage>{errors.accountHolderName}</FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">
                  Percentage (%)
                </FormLabel>
                <Input
                  name="percentage"
                  value={formData.percentage}
                  onChange={handleChange}
                  type="number"
                  placeholder="e.g. 18"
                  bg="gray.50"
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">
                  Mobile No
                </FormLabel>
                <Input
                  name="mobileNo"
                  value={formData.mobileNo}
                  onChange={handleChange}
                  type="number"
                  placeholder="Enter number"
                  bg="gray.50"
                />
              </FormControl>

              <FormControl gridColumn={{ md: "span 2", lg: "span 2" }}>
                <FormLabel fontSize="sm" fontWeight="medium">
                  Bank Address
                </FormLabel>
                <Input
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Enter full address"
                  bg="gray.50"
                />
              </FormControl>

              {/* UPI Section */}
              <FormControl gridColumn={{ md: "span 2", lg: "span 3" }}>
                <Divider my={4} />
                <Heading size="sm" color="gray.700" mb={4}>
                  UPI Payment Details <Badge colorScheme="gray">Optional</Badge>
                </Heading>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">
                  UPI ID
                </FormLabel>
                <Input
                  name="upiId"
                  value={formData.upiId}
                  onChange={handleChange}
                  placeholder="e.g. yourname@upi"
                  bg="gray.50"
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Enter your UPI ID for quick payments
                </Text>
              </FormControl>

              <FormControl gridColumn={{ md: "span 2", lg: "span 2" }}>
                <FormLabel fontSize="sm" fontWeight="medium">
                  UPI QR Code Image
                </FormLabel>
                <VStack align="stretch" spacing={3}>
                  <HStack spacing={3}>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleUpiImageChange}
                      disabled={isUploadingImage}
                      placeholder="Select QR code image"
                      pt={1}
                      bg="gray.50"
                    />
                    {isUploadingImage && (
                      <Spinner size="sm" color="blue.500" />
                    )}
                  </HStack>

                  {/* Display Uploaded UPI QR Image */}
                  {upiQrImageUrl && (
                    <Box
                      p={3}
                      bg="blue.50"
                      borderRadius="lg"
                      border="2px dashed"
                      borderColor="blue.300"
                    >
                      <VStack spacing={3} align="center">
                        <Image
                          src={upiQrImageUrl}
                          alt="UPI QR Code"
                          boxSize="150px"
                          objectFit="cover"
                          borderRadius="md"
                          border="1px solid"
                          borderColor="blue.200"
                        />
                        <HStack spacing={2}>
                          <Badge colorScheme="green">
                            QR Code Uploaded
                          </Badge>
                          <Button
                            size="xs"
                            colorScheme="red"
                            variant="outline"
                            leftIcon={<X size={14} />}
                            onClick={handleRemoveUpiImage}
                          >
                            Remove
                          </Button>
                        </HStack>
                        <Text fontSize="xs" color="gray.600">
                          Click "Remove" to change the QR code image
                        </Text>
                      </VStack>
                    </Box>
                  )}

                  {!upiQrImageUrl && (
                    <Box
                      p={3}
                      bg="gray.50"
                      borderRadius="lg"
                      border="2px dashed"
                      borderColor="gray.300"
                      textAlign="center"
                    >
                      <Upload size={20} style={{ margin: "0 auto" }} />
                      <Text fontSize="xs" color="gray.600" mt={2}>
                        No QR code image uploaded yet
                      </Text>
                    </Box>
                  )}
                </VStack>
              </FormControl>
            </SimpleGrid>

            <Divider my={8} />

            <Flex justify="flex-end" gap={4}>
              <Button
                onClick={() => navigate("/account-list")}
                variant="outline"
                colorScheme="gray"
              >
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                isLoading={loading || isUploadingImage}
                loadingText={location?.state ? "Updating..." : "Saving..."}
                onClick={handleSubmit}
                leftIcon={
                  location?.state ? <Edit size={18} /> : <Save size={18} />
                }
                px={8}
              >
                {location?.state ? "Update Account" : "Save Account"}
              </Button>
            </Flex>
          </CardBody>
        </Card>
      </Flex>
    </Box>
  );
};

export default AddAccount;
